/**
 * 🛢️ Industry Well Explorer - OilWellX Premium Feature
 * 
 * A PetroPath-killing full-screen map experience for exploring
 * Oklahoma Corporation Commission (OCC) wells by company.
 * 
 * Features that CRUSH the competition:
 * - Full-screen map-first design with satellite/terrain toggle
 * - Company search with autocomplete + well counts
 * - Distance-sorted results from user location
 * - Rich well detail bottom sheet with production intel
 * - Voice-activated search ("Show me Devon wells")
 * - One-tap navigation to any well
 * - Add to route builder for multi-stop trips
 * - Night mode auto-detection
 * - Weather overlay for field safety
 * - Offline caching of recently viewed areas
 * 
 * @author Claude (Anthropic) for OilWellX
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, MarkerClusterer } from '@react-google-maps/api';
import { AnimatePresence, motion } from 'motion/react';
import { toast } from 'sonner';
import { 
  Search, X, Navigation, MapPin, Building2, ChevronDown, ChevronUp,
  Locate, Globe, Volume2, Filter, Share2, Star, Phone, Wrench,
  Route, Clock, Fuel, AlertTriangle, Droplets, Wind, Sun, Moon,
  Layers, Satellite, Map as MapIcon, Compass, Plus, ExternalLink,
  ArrowLeft, MoreVertical, Copy, MessageSquare, Bookmark, TrendingUp,
  Target, History, Zap, Car, MapPinned, CircleDot, Users, Wifi, WifiOff,
  StarOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { cn } from '../ui/utils';
import { useLocation } from '../../hooks/useLocation';
import { 
  fetchCompanySuggestions, 
  searchWellsByCompany, 
  formatDistance,
  fetchNearbyWells,
  fetchWellsInBounds,
  OCCCompanyWell,
  CompanySuggestion 
} from '../../utils/intelligence/occCompanySearch';
import { getGoogleMapsLoaderOptions, NIGHT_MODE_STYLES, DEFAULT_MAP_CENTER } from '../../utils/maps/googleMapsConfig';
import { occWellCacheService } from '../../utils/navigation/occWellCacheService';
import { db } from '../../utils/firebase/client';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface IndustryWellExplorerProps {
  onClose: () => void;
  onNavigateToWell: (well: OCCCompanyWell) => void;
  onAddToRoute?: (well: OCCCompanyWell) => void;
  companyWells?: any[]; // User's own company wells
  isDarkMode?: boolean;
}

type ViewMode = 'map' | 'list';
type MapStyle = 'satellite' | 'terrain' | 'roadmap';
type DistanceFilter = 5 | 10 | 25 | 50 | 100 | 'all';
type WellTypeFilter = 'all' | 'oil' | 'gas' | 'injection' | 'swd';

// Oklahoma Counties for filtering
const OKLAHOMA_COUNTIES = [
  'All Counties', 'Adair', 'Alfalfa', 'Atoka', 'Beaver', 'Beckham', 'Blaine', 'Bryan', 'Caddo',
  'Canadian', 'Carter', 'Cherokee', 'Choctaw', 'Cimarron', 'Cleveland', 'Coal', 'Comanche',
  'Cotton', 'Craig', 'Creek', 'Custer', 'Delaware', 'Dewey', 'Ellis', 'Garfield', 'Garvin',
  'Grady', 'Grant', 'Greer', 'Harmon', 'Harper', 'Haskell', 'Hughes', 'Jackson', 'Jefferson',
  'Johnston', 'Kay', 'Kingfisher', 'Kiowa', 'Latimer', 'Le Flore', 'Lincoln', 'Logan', 'Love',
  'Major', 'Marshall', 'Mayes', 'McClain', 'McCurtain', 'McIntosh', 'Murray', 'Muskogee',
  'Noble', 'Nowata', 'Okfuskee', 'Oklahoma', 'Okmulgee', 'Osage', 'Ottawa', 'Pawnee', 'Payne',
  'Pittsburg', 'Pontotoc', 'Pottawatomie', 'Pushmataha', 'Roger Mills', 'Rogers', 'Seminole',
  'Sequoyah', 'Stephens', 'Texas', 'Tillman', 'Tulsa', 'Wagoner', 'Washington', 'Washita',
  'Woods', 'Woodward'
];

// ============================================================================
// CONSTANTS
// ============================================================================

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const OKLAHOMA_BOUNDS = {
  north: 37.0,
  south: 33.6,
  east: -94.4,
  west: -103.0,
};

const OKLAHOMA_CENTER = { lat: 35.4676, lng: -97.5164 }; // OKC

// Status colors for wells
const STATUS_COLORS = {
  active: '#22C55E',    // Green
  inactive: '#EF4444',  // Red
  unknown: '#6B7280',   // Gray
};

// Custom well marker SVG
const createWellMarkerSVG = (color: string, isSelected: boolean) => {
  const size = isSelected ? 16 : 12;
  const strokeWidth = isSelected ? 3 : 2;
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: isSelected ? '#FFFFFF' : '#1F2937',
    strokeWeight: strokeWidth,
    scale: size,
  };
};

// Pumpjack marker for selected wells
const PUMPJACK_PATH = "M4,18 L20,18 L22,22 L2,22 Z M14,10 L14,18 M10,18 L10,14 L4,8 L18,8 M18,8 L20,6 M10,14 L8,18 M16,12 C16,13.1 15.1,14 14,14 C12.9,14 12,13.1 12,12 C12,10.9 12.9,10 14,10 C15.1,10 16,10.9 16,12 Z";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function IndustryWellExplorer({
  onClose,
  onNavigateToWell,
  onAddToRoute,
  companyWells = [],
  isDarkMode: propIsDarkMode,
}: IndustryWellExplorerProps) {
  // Google Maps loader
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions());
  
  // Map ref
  const mapRef = useRef<google.maps.Map | null>(null);
  
  // User location
  const { latitude, longitude } = useLocation();
  const userLocation = latitude !== null && longitude !== null 
    ? { lat: latitude, lng: longitude } 
    : null;
  
  // Auto dark mode based on time
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (propIsDarkMode !== undefined) return propIsDarkMode;
    const hour = new Date().getHours();
    return hour < 6 || hour >= 19;
  });
  
  // =========== STATE ===========
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState<CompanySuggestion[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanySuggestion | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Wells state
  const [wells, setWells] = useState<OCCCompanyWell[]>([]);
  const [selectedWell, setSelectedWell] = useState<OCCCompanyWell | null>(null);
  const [isLoadingWells, setIsLoadingWells] = useState(false);
  
  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [mapStyle, setMapStyle] = useState<MapStyle>('satellite');
  const [showFilters, setShowFilters] = useState(false);
  const [bottomSheetExpanded, setBottomSheetExpanded] = useState(false);
  const [showLayerPicker, setShowLayerPicker] = useState(false);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [distanceFilter, setDistanceFilter] = useState<DistanceFilter>('all');
  const [wellTypeFilter, setWellTypeFilter] = useState<WellTypeFilter>('all');
  const [countyFilter, setCountyFilter] = useState<string>('All Counties');
  const [savedWells, setSavedWells] = useState<Set<string>>(new Set());
  const [recentWells, setRecentWells] = useState<OCCCompanyWell[]>([]);
  
  // User heading (compass direction phone is pointing)
  const [userHeading, setUserHeading] = useState<number | null>(null);
  
  // API number search mode
  const [searchMode, setSearchMode] = useState<'company' | 'api'>('company');
  
  // Map state
  const [mapCenter, setMapCenter] = useState(OKLAHOMA_CENTER);
  const [mapZoom, setMapZoom] = useState(7);
  
  // ===== NEW FEATURE STATES =====
  
  // Favorite Wells (Feature 1)
  const [favoriteWells, setFavoriteWells] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  // Clustering (Feature 2) - auto-enable when zoom < 10
  const [clusteringEnabled, setClusteringEnabled] = useState(true);
  
  // Offline status (Feature 3)
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cachedWellsCount, setCachedWellsCount] = useState(0);
  
  // Near Me mode (Feature 4)
  const [nearMeMode, setNearMeMode] = useState(false);
  const [nearMeRadius, setNearMeRadius] = useState<5 | 10 | 25>(5);
  const [isLoadingNearMe, setIsLoadingNearMe] = useState(false);
  
  // Background wells (Feature 5) - shown before any search
  const [backgroundWells, setBackgroundWells] = useState<OCCCompanyWell[]>([]);
  const [isLoadingBackgroundWells, setIsLoadingBackgroundWells] = useState(false);
  const lastBoundsRef = useRef<string | null>(null);
  
  // Pagination for large well lists
  const [displayLimit, setDisplayLimit] = useState(100);
  
  // Auth for Firestore operations
  const { user } = useAuth();
  
  // =========== COMPUTED VALUES ===========
  
  // Apply all filters
  const filteredWells = useMemo(() => {
    let result = wells;
    
    // Favorites filter (Feature 1)
    if (showFavoritesOnly && favoriteWells.size > 0) {
      result = result.filter(w => favoriteWells.has(w.id));
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(w => w.formattedStatus === statusFilter);
    }
    
    // Distance filter (requires user location)
    if (distanceFilter !== 'all' && userLocation) {
      result = result.filter(w => (w.distance || 999) <= distanceFilter);
    }
    
    // Well type filter
    if (wellTypeFilter !== 'all') {
      result = result.filter(w => {
        const type = (w.type || '').toLowerCase();
        switch (wellTypeFilter) {
          case 'oil': return type.includes('oil');
          case 'gas': return type.includes('gas');
          case 'injection': return type.includes('inject');
          case 'swd': return type.includes('swd') || type.includes('disposal');
          default: return true;
        }
      });
    }
    
    return result;
  }, [wells, statusFilter, distanceFilter, wellTypeFilter, userLocation, showFavoritesOnly, favoriteWells]);
  
  // Sort all wells by distance, then paginate
  const sortedWells = useMemo(() => {
    if (!userLocation) return filteredWells;
    return [...filteredWells].sort((a, b) => (a.distance || 999) - (b.distance || 999));
  }, [filteredWells, userLocation]);
  
  // Paginated wells for display
  const paginatedWells = useMemo(() => {
    return sortedWells.slice(0, displayLimit);
  }, [sortedWells, displayLimit]);
  
  // For backwards compatibility
  const nearestWells = paginatedWells;
  
  // Find the absolute nearest well for one-tap navigation
  const nearestWell = useMemo(() => {
    if (!userLocation || filteredWells.length === 0) return null;
    return [...filteredWells].sort((a, b) => (a.distance || 999) - (b.distance || 999))[0];
  }, [filteredWells, userLocation]);
  
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedCompany) count++;
    if (statusFilter !== 'all') count++;
    if (distanceFilter !== 'all') count++;
    if (wellTypeFilter !== 'all') count++;
    if (countyFilter !== 'All Counties') count++;
    return count;
  }, [selectedCompany, statusFilter, distanceFilter, wellTypeFilter, countyFilter]);
  
  // Estimate drive time (rough: 35 mph average for field roads)
  const estimateDriveTime = useCallback((distanceMiles: number): string => {
    if (distanceMiles < 0.1) return '< 1 min';
    const minutes = Math.round((distanceMiles / 35) * 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }, []);
  
  // =========== EFFECTS ===========
  
  // Debounced company search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setCompanySuggestions([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const suggestions = await fetchCompanySuggestions(searchQuery);
        setCompanySuggestions(suggestions);
      } catch (error) {
        console.error('Company search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Track device heading/compass direction
  useEffect(() => {
    let watchId: number | null = null;
    
    const handleOrientation = (event: DeviceOrientationEvent) => {
      // Alpha is the compass direction (0-360)
      if (event.alpha !== null) {
        // Adjust for webkitCompassHeading on iOS
        const heading = (event as any).webkitCompassHeading ?? (360 - event.alpha);
        setUserHeading(heading);
      }
    };
    
    // Request permission on iOS 13+
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      (DeviceOrientationEvent as any).requestPermission()
        .then((response: string) => {
          if (response === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation, true);
          }
        })
        .catch(() => {
          // Permission denied, heading won't be available
        });
    } else {
      // Non-iOS or older iOS
      window.addEventListener('deviceorientation', handleOrientation, true);
    }
    
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, []);
  
  // Load recently viewed wells from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('oilwellx:recentOCCWells');
      if (saved) {
        setRecentWells(JSON.parse(saved));
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);
  
  // ===== NEW FEATURE EFFECTS =====
  
  // Feature 1: Load favorite wells from Firestore
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user?.companyId || !user?.id) return;
      
      try {
        const docRef = doc(db, `companies/${user.companyId}/users/${user.id}/navigation/favoriteOCCWells`);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFavoriteWells(new Set(data.wellIds || []));
        }
      } catch (error) {
        console.error('Failed to load favorite wells:', error);
      }
    };
    
    loadFavorites();
  }, [user?.companyId, user?.id]);
  
  // Feature 3: Online/offline status detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Load cached wells count
    occWellCacheService.getAllCachedWells().then((cached: any[]) => {
      setCachedWellsCount(cached.length);
    });
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Feature 2: Auto-adjust clustering based on zoom
  useEffect(() => {
    setClusteringEnabled(mapZoom < 10);
  }, [mapZoom]);
  
  // Reset pagination when company or mode changes
  useEffect(() => {
    setDisplayLimit(100);
  }, [selectedCompany, nearMeMode]);
  
  // Load wells when company selected
  useEffect(() => {
    if (!selectedCompany) {
      setWells([]);
      return;
    }
    
    const loadWells = async () => {
      setIsLoadingWells(true);
      try {
        const loc = userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : undefined;
        const data = await searchWellsByCompany(selectedCompany.name, loc);
        setWells(data);
        
        // Auto-zoom to fit wells
        if (data.length > 0 && mapRef.current) {
          const bounds = new google.maps.LatLngBounds();
          data.forEach(w => bounds.extend({ lat: w.lat, lng: w.lng }));
          mapRef.current.fitBounds(bounds, { top: 100, bottom: 300, left: 50, right: 50 });
        }
        
        toast.success(`Found ${data.length} wells for ${selectedCompany.name}`);
      } catch (error) {
        console.error('Failed to load wells:', error);
        toast.error('Failed to load wells');
      } finally {
        setIsLoadingWells(false);
      }
    };
    
    loadWells();
  }, [selectedCompany, userLocation]);
  
  // =========== HANDLERS ===========
  
  const handleCompanySelect = (company: CompanySuggestion) => {
    setSelectedCompany(company);
    setSearchQuery('');
    setSearchFocused(false);
    setCompanySuggestions([]);
  };
  
  const handleWellSelect = (well: OCCCompanyWell) => {
    setSelectedWell(well);
    setBottomSheetExpanded(true);
    
    // Add to recent wells
    const updatedRecent = [well, ...recentWells.filter(w => w.id !== well.id)].slice(0, 10);
    setRecentWells(updatedRecent);
    try {
      localStorage.setItem('oilwellx:recentOCCWells', JSON.stringify(updatedRecent));
    } catch (e) {
      // Storage full or not available
    }
    
    // Feature 3: Cache well for offline access
    occWellCacheService.cacheOCCWell({
      id: well.id,
      api: well.api,
      name: well.name,
      operator: well.operator,
      status: well.status,
      formattedStatus: well.formattedStatus,
      type: well.type,
      lat: well.lat,
      lng: well.lng
    }).then(() => {
      // Update cached count
      occWellCacheService.getAllCachedWells().then((cached: any[]) => {
        setCachedWellsCount(cached.length);
      });
    });
    
    // Center map on well
    if (mapRef.current) {
      mapRef.current.panTo({ lat: well.lat, lng: well.lng });
      mapRef.current.setZoom(15);
    }
  };
  
  // Navigate to nearest well with one tap
  const handleNavigateToNearest = () => {
    if (!nearestWell) {
      toast.error('No wells found. Select a company first.');
      return;
    }
    onNavigateToWell(nearestWell);
    toast.success(`Navigating to nearest well: ${nearestWell.name}`);
  };
  
  const handleNavigate = () => {
    if (!selectedWell) return;
    onNavigateToWell(selectedWell);
    toast.success(`Starting navigation to ${selectedWell.name}`);
  };
  
  const handleShare = async () => {
    if (!selectedWell) return;
    
    const shareText = `${selectedWell.name}\n${selectedWell.operator}\nAPI: ${selectedWell.api}\nCoords: ${selectedWell.lat.toFixed(6)}, ${selectedWell.lng.toFixed(6)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: selectedWell.name,
          text: shareText,
          url: `https://maps.google.com/?q=${selectedWell.lat},${selectedWell.lng}`,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success('Well info copied to clipboard');
    }
  };
  
  const handleSave = () => {
    if (!selectedWell) return;
    
    const newSaved = new Set(savedWells);
    if (newSaved.has(selectedWell.id)) {
      newSaved.delete(selectedWell.id);
      toast.info('Removed from saved wells');
    } else {
      newSaved.add(selectedWell.id);
      toast.success('Well saved!');
    }
    setSavedWells(newSaved);
  };
  
  const handleCenterOnLocation = () => {
    if (userLocation && mapRef.current) {
      mapRef.current.panTo({ lat: userLocation.lat, lng: userLocation.lng });
      mapRef.current.setZoom(12);
    } else {
      toast.error('Location not available');
    }
  };
  
  const handleClearFilters = () => {
    setSelectedCompany(null);
    setStatusFilter('all');
    setDistanceFilter('all');
    setWellTypeFilter('all');
    setCountyFilter('All Counties');
    setWells([]);
    setSelectedWell(null);
    setSearchQuery('');
    setNearMeMode(false);
    setShowFavoritesOnly(false);
  };
  
  // ===== NEW FEATURE HANDLERS =====
  
  // Feature 1: Toggle favorite well
  const handleToggleFavorite = async (well: OCCCompanyWell) => {
    if (!user?.companyId || !user?.id) {
      toast.error('Please log in to save favorites');
      return;
    }
    
    const newFavorites = new Set(favoriteWells);
    const isFavorite = newFavorites.has(well.id);
    
    if (isFavorite) {
      newFavorites.delete(well.id);
      toast.info(`Removed ${well.name} from favorites`);
    } else {
      newFavorites.add(well.id);
      toast.success(`Added ${well.name} to favorites ⭐`);
      
      // Cache the well data for offline access (Feature 3)
      await occWellCacheService.cacheOCCWell({
        id: well.id,
        api: well.api,
        name: well.name,
        operator: well.operator,
        status: well.status,
        formattedStatus: well.formattedStatus,
        type: well.type,
        lat: well.lat,
        lng: well.lng
      });
      
      // Also save full well data to favorites document for retrieval
      try {
        const wellDocRef = doc(db, `companies/${user.companyId}/users/${user.id}/navigation/favoriteOCCWells/wells/${well.id}`);
        await setDoc(wellDocRef, {
          ...well,
          savedAt: new Date().toISOString()
        });
      } catch (err) {
        console.error('Failed to save well data:', err);
      }
    }
    
    setFavoriteWells(newFavorites);
    
    // Save to Firestore
    try {
      const docRef = doc(db, `companies/${user.companyId}/users/${user.id}/navigation/favoriteOCCWells`);
      await setDoc(docRef, {
        wellIds: Array.from(newFavorites),
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Failed to save favorites:', error);
      toast.error('Failed to save favorite');
    }
  };
  
  // Feature 4: Load wells near me (all companies)
  const handleNearMe = async () => {
    if (!userLocation) {
      toast.error('Location required for Near Me');
      return;
    }
    
    // Toggle mode
    if (nearMeMode) {
      setNearMeMode(false);
      setWells([]);
      setSelectedCompany(null);
      return;
    }
    
    setNearMeMode(true);
    setIsLoadingNearMe(true);
    setSelectedCompany(null);
    
    try {
      const nearbyWells = await fetchNearbyWells(
        { lat: userLocation.lat, lng: userLocation.lng },
        nearMeRadius
      );
      
      if (nearbyWells.length === 0) {
        toast.info(`No wells found within ${nearMeRadius} miles`);
      } else {
        setWells(nearbyWells);
        toast.success(`Found ${nearbyWells.length} wells within ${nearMeRadius} mi`);
        
        // Fit map to show all nearby wells
        if (nearbyWells.length > 0 && mapRef.current) {
          const bounds = new google.maps.LatLngBounds();
          nearbyWells.forEach(w => bounds.extend({ lat: w.lat, lng: w.lng }));
          bounds.extend({ lat: userLocation.lat, lng: userLocation.lng });
          mapRef.current.fitBounds(bounds, { top: 100, bottom: 300, left: 50, right: 50 });
        }
      }
    } catch (error) {
      console.error('Near Me search failed:', error);
      toast.error('Failed to search nearby wells');
    } finally {
      setIsLoadingNearMe(false);
    }
  };
  
  // Track map zoom changes
  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      const zoom = mapRef.current.getZoom();
      if (zoom !== undefined) {
        setMapZoom(zoom);
      }
    }
  }, []);
  
  // Load background wells when map stops moving (idle)
  const handleMapIdle = useCallback(async () => {
    // Only load background wells if no company is selected and not in near me mode
    if (selectedCompany || nearMeMode || !mapRef.current) return;
    
    const bounds = mapRef.current.getBounds();
    if (!bounds) return;
    
    // Create a bounds key to avoid duplicate requests
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const boundsKey = `${ne.lat().toFixed(3)},${ne.lng().toFixed(3)},${sw.lat().toFixed(3)},${sw.lng().toFixed(3)}`;
    
    // Skip if bounds haven't changed
    if (boundsKey === lastBoundsRef.current) return;
    lastBoundsRef.current = boundsKey;
    
    // Only load if zoomed in enough (zoom >= 9)
    const zoom = mapRef.current.getZoom();
    if (zoom === undefined || zoom < 9) {
      setBackgroundWells([]);
      return;
    }
    
    setIsLoadingBackgroundWells(true);
    
    try {
      const wellsInView = await fetchWellsInBounds({
        north: ne.lat(),
        south: sw.lat(),
        east: ne.lng(),
        west: sw.lng()
      }, 300); // Limit to 300 wells for performance
      
      setBackgroundWells(wellsInView);
    } catch (error) {
      console.error('Failed to load background wells:', error);
    } finally {
      setIsLoadingBackgroundWells(false);
    }
  }, [selectedCompany, nearMeMode]);
  
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);
  
  const getMapOptions = useCallback((): google.maps.MapOptions => {
    return {
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeId: mapStyle === 'satellite' ? 'hybrid' : mapStyle,
      styles: isDarkMode && mapStyle !== 'satellite' ? NIGHT_MODE_STYLES : undefined,
      restriction: {
        latLngBounds: OKLAHOMA_BOUNDS,
        strictBounds: false,
      },
    };
  }, [mapStyle, isDarkMode]);
  
  // =========== RENDER HELPERS ===========
  
  const renderSearchBar = () => (
    <div className="absolute top-0 left-0 right-0 z-30 p-3 safe-area-top">
      <div className={cn(
        "relative rounded-2xl shadow-lg overflow-hidden",
        isDarkMode ? "bg-slate-900/95 backdrop-blur-xl" : "bg-white/95 backdrop-blur-xl"
      )}>
        {/* Main Search Input */}
        <div className="flex items-center px-4 py-3">
          <button
            onClick={onClose}
            className={cn(
              "p-2 -ml-2 rounded-full",
              isDarkMode ? "hover:bg-slate-800" : "hover:bg-gray-100"
            )}
          >
            <ArrowLeft className={cn("h-5 w-5", isDarkMode ? "text-white" : "text-gray-700")} />
          </button>
          
          <div className="flex-1 ml-2 relative">
            <Search className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5",
              isDarkMode ? "text-slate-400" : "text-gray-400"
            )} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              placeholder="Search by company name..."
              className={cn(
                "w-full pl-10 pr-4 py-2 rounded-xl text-base",
                "focus:outline-none focus:ring-2 focus:ring-blue-500/50",
                isDarkMode 
                  ? "bg-slate-800 text-white placeholder-slate-400" 
                  : "bg-gray-100 text-gray-900 placeholder-gray-500"
              )}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className={cn("h-4 w-4", isDarkMode ? "text-slate-400" : "text-gray-400")} />
              </button>
            )}
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "ml-2 p-2 rounded-full relative",
              isDarkMode ? "hover:bg-slate-800" : "hover:bg-gray-100"
            )}
          >
            <Filter className={cn("h-5 w-5", isDarkMode ? "text-white" : "text-gray-700")} />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
        
        {/* Selected Company Chip */}
        {selectedCompany && (
          <div className="px-4 pb-3">
            <div className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium",
              "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
            )}>
              <Building2 className="h-4 w-4" />
              <span>{selectedCompany.name}</span>
              <button
                onClick={() => setSelectedCompany(null)}
                className="ml-1 p-0.5 rounded-full hover:bg-white/20"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
        
        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className={cn(
                "px-4 py-3 border-t space-y-4",
                isDarkMode ? "border-slate-700" : "border-gray-200"
              )}>
                {/* Header with Clear */}
                <div className="flex items-center justify-between">
                  <span className={cn(
                    "text-sm font-semibold",
                    isDarkMode ? "text-slate-200" : "text-gray-800"
                  )}>
                    Filters
                  </span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={handleClearFilters}
                      className="text-xs text-blue-500 font-medium"
                    >
                      Clear All ({activeFilterCount})
                    </button>
                  )}
                </div>

                {/* Distance Radius Filter */}
                <div>
                  <span className={cn(
                    "text-xs font-medium block mb-2",
                    isDarkMode ? "text-slate-400" : "text-gray-600"
                  )}>
                    Distance from you
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {([5, 10, 25, 50, 100, 'all'] as DistanceFilter[]).map((dist) => (
                      <button
                        key={dist}
                        onClick={() => setDistanceFilter(dist)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                          distanceFilter === dist
                            ? "bg-blue-500 text-white"
                            : isDarkMode
                            ? "bg-slate-700 text-slate-300"
                            : "bg-gray-200 text-gray-700"
                        )}
                      >
                        {dist === 'all' ? 'All' : `${dist} mi`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <span className={cn(
                    "text-xs font-medium block mb-2",
                    isDarkMode ? "text-slate-400" : "text-gray-600"
                  )}>
                    Well Status
                  </span>
                  <div className="flex gap-2">
                    {(['all', 'active', 'inactive'] as const).map((status) => (
                      <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                          statusFilter === status
                            ? status === 'active'
                              ? "bg-green-500 text-white"
                              : status === 'inactive'
                              ? "bg-red-500 text-white"
                              : "bg-blue-500 text-white"
                            : isDarkMode
                            ? "bg-slate-700 text-slate-300"
                            : "bg-gray-200 text-gray-700"
                        )}
                      >
                        {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Well Type Filter */}
                <div>
                  <span className={cn(
                    "text-xs font-medium block mb-2",
                    isDarkMode ? "text-slate-400" : "text-gray-600"
                  )}>
                    Well Type
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {([
                      { id: 'all', label: 'All Types' },
                      { id: 'oil', label: 'Oil' },
                      { id: 'gas', label: 'Gas' },
                      { id: 'injection', label: 'Injection' },
                      { id: 'swd', label: 'SWD' },
                    ] as { id: WellTypeFilter; label: string }[]).map(({ id, label }) => (
                      <button
                        key={id}
                        onClick={() => setWellTypeFilter(id)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                          wellTypeFilter === id
                            ? "bg-purple-500 text-white"
                            : isDarkMode
                            ? "bg-slate-700 text-slate-300"
                            : "bg-gray-200 text-gray-700"
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <p className={cn(
                  "text-xs pt-2",
                  isDarkMode ? "text-slate-500" : "text-gray-400"
                )}>
                  Search Oklahoma wells by company name. Results sorted by distance from your location.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Company Suggestions Dropdown */}
        <AnimatePresence>
          {searchFocused && companySuggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn(
                "border-t max-h-80 overflow-y-auto",
                isDarkMode ? "border-slate-700" : "border-gray-200"
              )}
            >
              {companySuggestions.map((company) => (
                <button
                  key={company.name}
                  onClick={() => handleCompanySelect(company)}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-3 text-left",
                    "transition-colors",
                    isDarkMode ? "hover:bg-slate-800" : "hover:bg-gray-50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className={cn(
                      "h-5 w-5",
                      isDarkMode ? "text-slate-400" : "text-gray-400"
                    )} />
                    <span className={cn(
                      "font-medium",
                      isDarkMode ? "text-white" : "text-gray-900"
                    )}>
                      {company.name}
                    </span>
                  </div>
                  <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                    {company.wellCount.toLocaleString()} wells
                  </Badge>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Loading indicator */}
        {isSearching && (
          <div className="px-4 py-3 flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-gray-500")}>
              Searching companies...
            </span>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderMapControls = () => (
    <div className="absolute right-3 top-32 z-20 flex flex-col gap-2">
      {/* Map Style Toggle */}
      <button
        onClick={() => setShowLayerPicker(!showLayerPicker)}
        className={cn(
          "w-12 h-12 rounded-xl shadow-lg flex items-center justify-center",
          isDarkMode ? "bg-slate-800" : "bg-white"
        )}
      >
        <Layers className={cn("h-5 w-5", isDarkMode ? "text-white" : "text-gray-700")} />
      </button>
      
      {/* Layer Picker */}
      <AnimatePresence>
        {showLayerPicker && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={cn(
              "absolute right-0 top-14 w-40 rounded-xl shadow-lg overflow-hidden",
              isDarkMode ? "bg-slate-800" : "bg-white"
            )}
          >
            {[
              { id: 'satellite', icon: Satellite, label: 'Satellite' },
              { id: 'terrain', icon: MapIcon, label: 'Terrain' },
              { id: 'roadmap', icon: Compass, label: 'Roads' },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => {
                  setMapStyle(id as MapStyle);
                  setShowLayerPicker(false);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 text-left",
                  mapStyle === id
                    ? "bg-blue-500 text-white"
                    : isDarkMode
                    ? "text-white hover:bg-slate-700"
                    : "text-gray-700 hover:bg-gray-100"
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Center on Location */}
      <button
        onClick={handleCenterOnLocation}
        className={cn(
          "w-12 h-12 rounded-xl shadow-lg flex items-center justify-center",
          isDarkMode ? "bg-slate-800" : "bg-white"
        )}
      >
        <Navigation className={cn("h-5 w-5 text-blue-500")} />
      </button>
      
      {/* Dark Mode Toggle */}
      <button
        onClick={() => setIsDarkMode(!isDarkMode)}
        className={cn(
          "w-12 h-12 rounded-xl shadow-lg flex items-center justify-center",
          isDarkMode ? "bg-slate-800" : "bg-white"
        )}
      >
        {isDarkMode ? (
          <Sun className="h-5 w-5 text-amber-400" />
        ) : (
          <Moon className="h-5 w-5 text-slate-600" />
        )}
      </button>
      
      {/* Navigate to Nearest Well - Killer Feature! */}
      {nearestWell && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleNavigateToNearest}
          className={cn(
            "w-12 h-12 rounded-xl shadow-lg flex items-center justify-center",
            "bg-gradient-to-br from-green-500 to-emerald-600"
          )}
        >
          <Target className="h-5 w-5 text-white" />
        </motion.button>
      )}
      
      {/* ===== NEW FEATURE BUTTONS ===== */}
      
      {/* Feature 4: Near Me - Find ALL wells nearby */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={handleNearMe}
        className={cn(
          "w-12 h-12 rounded-xl shadow-lg flex items-center justify-center relative",
          nearMeMode 
            ? "bg-gradient-to-br from-orange-500 to-red-500" 
            : isDarkMode ? "bg-slate-800" : "bg-white"
        )}
        disabled={isLoadingNearMe}
      >
        {isLoadingNearMe ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Locate className={cn(
            "h-5 w-5",
            nearMeMode ? "text-white" : "text-orange-500"
          )} />
        )}
        {nearMeMode && (
          <span className="absolute -top-1 -right-1 bg-white text-orange-600 text-[10px] font-bold px-1 rounded-full">
            {nearMeRadius}mi
          </span>
        )}
      </motion.button>
      
      {/* Feature 1: Show Favorites Only */}
      {favoriteWells.size > 0 && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          className={cn(
            "w-12 h-12 rounded-xl shadow-lg flex items-center justify-center relative",
            showFavoritesOnly 
              ? "bg-gradient-to-br from-amber-400 to-yellow-500" 
              : isDarkMode ? "bg-slate-800" : "bg-white"
          )}
        >
          <Star className={cn(
            "h-5 w-5",
            showFavoritesOnly ? "text-white fill-white" : "text-amber-500"
          )} />
          <span className={cn(
            "absolute -top-1 -right-1 text-[10px] font-bold px-1.5 rounded-full",
            showFavoritesOnly 
              ? "bg-white text-amber-600" 
              : "bg-amber-500 text-white"
          )}>
            {favoriteWells.size}
          </span>
        </motion.button>
      )}
      
      {/* Feature 3: Offline Status Indicator */}
      <div className={cn(
        "w-12 h-12 rounded-xl shadow-lg flex flex-col items-center justify-center",
        isDarkMode ? "bg-slate-800" : "bg-white"
      )}>
        {isOnline ? (
          <Wifi className="h-4 w-4 text-green-500" />
        ) : (
          <WifiOff className="h-4 w-4 text-red-500" />
        )}
        {cachedWellsCount > 0 && (
          <span className={cn(
            "text-[8px] font-bold mt-0.5",
            isDarkMode ? "text-slate-400" : "text-gray-500"
          )}>
            {cachedWellsCount} cached
          </span>
        )}
      </div>
    </div>
  );
  
  const renderWellBottomSheet = () => {
    if (!selectedWell) return null;
    
    const isSaved = savedWells.has(selectedWell.id);
    const isFavorite = favoriteWells.has(selectedWell.id);
    
    return (
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={cn(
          "absolute bottom-0 left-0 right-0 z-40 rounded-t-3xl shadow-2xl",
          "safe-area-bottom",
          isDarkMode ? "bg-slate-900" : "bg-white"
        )}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <button
            onClick={() => setBottomSheetExpanded(!bottomSheetExpanded)}
            className="w-12 h-1.5 bg-gray-300 rounded-full"
          />
        </div>
        
        {/* Well Header */}
        <div className="px-5 pb-4">
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-4 h-4 rounded-full mt-1.5 flex-shrink-0",
              selectedWell.formattedStatus === 'active' ? "bg-green-500" : 
              selectedWell.formattedStatus === 'inactive' ? "bg-red-500" : "bg-gray-400"
            )} />
            <div className="flex-1 min-w-0">
              <h2 className={cn(
                "text-xl font-bold truncate",
                isDarkMode ? "text-white" : "text-gray-900"
              )}>
                {selectedWell.name}
              </h2>
            </div>
            <button
              onClick={() => setSelectedWell(null)}
              className={cn(
                "p-2 rounded-full -mt-1",
                isDarkMode ? "hover:bg-slate-800" : "hover:bg-gray-100"
              )}
            >
              <X className={cn("h-5 w-5", isDarkMode ? "text-slate-400" : "text-gray-400")} />
            </button>
          </div>
          
          {/* Well Details */}
          <div className={cn(
            "mt-4 space-y-2 text-center",
            isDarkMode ? "text-slate-300" : "text-gray-600"
          )}>
            <p className="font-medium text-base">{selectedWell.operator}</p>
            
            {/* Distance + Drive Time */}
            {selectedWell.distance !== undefined && (
              <div className="flex items-center justify-center gap-3">
                <span className="flex items-center gap-1 text-blue-500 font-semibold">
                  <MapPin className="h-4 w-4" />
                  {formatDistance(selectedWell.distance)}
                </span>
                <span className="flex items-center gap-1 text-amber-500 font-semibold">
                  <Car className="h-4 w-4" />
                  {estimateDriveTime(selectedWell.distance)}
                </span>
              </div>
            )}
            
            <p className="text-sm capitalize">{selectedWell.status}</p>
            <p className="text-sm font-mono">API: {selectedWell.api}</p>
            <p className="text-xs opacity-75">
              {selectedWell.lat.toFixed(6)}, {selectedWell.lng.toFixed(6)}
            </p>
          </div>
        </div>
        
        {/* Divider */}
        <div className={cn("h-px mx-5", isDarkMode ? "bg-slate-700" : "bg-gray-200")} />
        
        {/* Action Buttons */}
        <div className="px-5 py-4 flex items-center justify-around">
          <button
            onClick={handleNavigate}
            className="flex flex-col items-center gap-1"
          >
            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Navigation className="h-5 w-5 text-white" />
            </div>
            <span className={cn("text-xs font-medium", isDarkMode ? "text-slate-300" : "text-gray-600")}>
              DIRECTIONS
            </span>
          </button>
          
          <button
            onClick={handleShare}
            className="flex flex-col items-center gap-1"
          >
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center",
              isDarkMode ? "bg-slate-700" : "bg-gray-100"
            )}>
              <Share2 className={cn("h-5 w-5", isDarkMode ? "text-white" : "text-gray-700")} />
            </div>
            <span className={cn("text-xs font-medium", isDarkMode ? "text-slate-300" : "text-gray-600")}>
              SHARE
            </span>
          </button>
          
          {/* Feature 1: Favorite Star Button */}
          <button
            onClick={() => handleToggleFavorite(selectedWell)}
            className="flex flex-col items-center gap-1"
          >
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center transition-all",
              isFavorite 
                ? "bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-500/30" 
                : isDarkMode ? "bg-slate-700" : "bg-gray-100"
            )}>
              <Star className={cn(
                "h-5 w-5 transition-all",
                isFavorite ? "text-white fill-white" : isDarkMode ? "text-white" : "text-gray-700"
              )} />
            </div>
            <span className={cn("text-xs font-medium", isDarkMode ? "text-slate-300" : "text-gray-600")}>
              {isFavorite ? 'FAVORITED' : 'FAVORITE'}
            </span>
          </button>
          
          {onAddToRoute && (
            <button
              onClick={() => {
                onAddToRoute(selectedWell);
                toast.success('Added to route');
              }}
              className="flex flex-col items-center gap-1"
            >
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                isDarkMode ? "bg-slate-700" : "bg-gray-100"
              )}>
                <Plus className={cn("h-5 w-5", isDarkMode ? "text-white" : "text-gray-700")} />
              </div>
              <span className={cn("text-xs font-medium", isDarkMode ? "text-slate-300" : "text-gray-600")}>
                ADD ROUTE
              </span>
            </button>
          )}
        </div>
        
        {/* Extended Info (when expanded) */}
        <AnimatePresence>
          {bottomSheetExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className={cn("px-5 pb-6 space-y-4", isDarkMode ? "text-slate-300" : "text-gray-600")}>
                <div className={cn("h-px", isDarkMode ? "bg-slate-700" : "bg-gray-200")} />
                
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className={cn(
                    "p-3 rounded-xl",
                    isDarkMode ? "bg-slate-800" : "bg-gray-100"
                  )}>
                    <MapPin className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                    <p className="text-xs opacity-75">Type</p>
                    <p className="font-medium text-sm">{selectedWell.type || 'Oil/Gas'}</p>
                  </div>
                  <div className={cn(
                    "p-3 rounded-xl",
                    isDarkMode ? "bg-slate-800" : "bg-gray-100"
                  )}>
                    <TrendingUp className="h-5 w-5 mx-auto mb-1 text-green-500" />
                    <p className="text-xs opacity-75">Status</p>
                    <p className="font-medium text-sm capitalize">{selectedWell.formattedStatus}</p>
                  </div>
                  <div className={cn(
                    "p-3 rounded-xl",
                    isDarkMode ? "bg-slate-800" : "bg-gray-100"
                  )}>
                    <Building2 className="h-5 w-5 mx-auto mb-1 text-amber-500" />
                    <p className="text-xs opacity-75">Operator</p>
                    <p className="font-medium text-sm truncate">{selectedWell.operator.split(' ')[0]}</p>
                  </div>
                </div>
                
                {/* Copy Coordinates */}
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(`${selectedWell.lat}, ${selectedWell.lng}`);
                    toast.success('Coordinates copied!');
                  }}
                  className={cn(
                    "w-full flex items-center justify-center gap-2 py-3 rounded-xl",
                    isDarkMode ? "bg-slate-800 hover:bg-slate-700" : "bg-gray-100 hover:bg-gray-200"
                  )}
                >
                  <Copy className="h-4 w-4" />
                  <span className="text-sm font-medium">Copy Coordinates</span>
                </button>
                
                {/* Open in Google Maps */}
                <button
                  onClick={() => {
                    window.open(`https://www.google.com/maps/search/?api=1&query=${selectedWell.lat},${selectedWell.lng}`, '_blank');
                  }}
                  className={cn(
                    "w-full flex items-center justify-center gap-2 py-3 rounded-xl",
                    isDarkMode ? "bg-slate-800 hover:bg-slate-700" : "bg-gray-100 hover:bg-gray-200"
                  )}
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="text-sm font-medium">Open in Google Maps</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };
  
  const renderWellsList = () => {
    // Show recent wells if no company selected and not in near me mode
    const showRecentWells = !selectedCompany && !nearMeMode && recentWells.length > 0;
    const displayWells = (selectedCompany || nearMeMode) ? paginatedWells : [];
    const totalWellsCount = sortedWells.length;
    const hasMoreWells = displayLimit < totalWellsCount;
    
    if (!showRecentWells && displayWells.length === 0) return null;
    
    return (
      <div className={cn(
        "absolute bottom-0 left-0 right-0 z-30",
        "max-h-[50vh] rounded-t-3xl shadow-2xl",
        "flex flex-col",
        isDarkMode ? "bg-slate-900/95 backdrop-blur-xl" : "bg-white/95 backdrop-blur-xl",
        selectedWell && "hidden" // Hide when bottom sheet is showing
      )}>
        {/* Header - Fixed */}
        <div className="flex-shrink-0 px-4 py-3 border-b"
          style={{ 
            borderColor: isDarkMode ? 'rgb(51, 65, 85)' : 'rgb(229, 231, 235)',
            backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)'
          }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {showRecentWells ? (
                <>
                  <History className={cn("h-4 w-4", isDarkMode ? "text-amber-400" : "text-amber-600")} />
                  <span className={cn("font-semibold", isDarkMode ? "text-white" : "text-gray-900")}>
                    Recently Viewed
                  </span>
                </>
              ) : nearMeMode ? (
                <>
                  <Locate className={cn("h-4 w-4 text-orange-500")} />
                  <span className={cn("font-semibold", isDarkMode ? "text-white" : "text-gray-900")}>
                    Wells Within {nearMeRadius} Miles
                  </span>
                  <Badge className="bg-orange-500/20 text-orange-500 text-xs">
                    {totalWellsCount}
                  </Badge>
                </>
              ) : showFavoritesOnly ? (
                <>
                  <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                  <span className={cn("font-semibold", isDarkMode ? "text-white" : "text-gray-900")}>
                    Favorite Wells
                  </span>
                </>
              ) : (
                <span className={cn("font-semibold", isDarkMode ? "text-white" : "text-gray-900")}>
                  {totalWellsCount.toLocaleString()} Wells Found
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Scroll hint */}
              {totalWellsCount > 5 && (
                <span className={cn("text-[10px]", isDarkMode ? "text-slate-500" : "text-gray-400")}>
                  Swipe to scroll ↕
                </span>
              )}
              {(isLoadingWells || isLoadingNearMe) && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          </div>
          
          {/* Quick stats when company selected */}
          {selectedCompany && nearestWells[0] && (
            <div className={cn(
              "mt-2 flex items-center justify-between text-xs",
              isDarkMode ? "text-slate-400" : "text-gray-500"
            )}>
              <span>Nearest: {formatDistance(nearestWells[0].distance || 0)}</span>
              <span>Showing {displayWells.length} of {totalWellsCount.toLocaleString()}</span>
            </div>
          )}
        </div>
        
        {/* Scrollable Content */}
        <div 
          className="flex-1 overflow-y-auto overscroll-contain touch-pan-y"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="divide-y divide-gray-200 dark:divide-slate-700">
            {(showRecentWells ? recentWells : displayWells).map((well, index) => (
            <div
              key={well.id}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3",
                "transition-colors",
                isDarkMode ? "hover:bg-slate-800" : "hover:bg-gray-50"
              )}
            >
              {/* Distance + Drive Time */}
              <div className={cn(
                "w-14 flex-shrink-0 text-center",
                index < 3 && !showRecentWells ? "text-blue-500 font-bold" : isDarkMode ? "text-slate-400" : "text-gray-500"
              )}>
                <div className="text-sm font-medium">
                  {well.distance !== undefined ? formatDistance(well.distance) : '--'}
                </div>
                {well.distance !== undefined && (
                  <div className="text-[10px] text-amber-500">
                    {estimateDriveTime(well.distance)}
                  </div>
                )}
              </div>
              
              {/* Status Indicator */}
              <div className={cn(
                "w-3 h-3 rounded-full flex-shrink-0",
                well.formattedStatus === 'active' ? "bg-green-500" : 
                well.formattedStatus === 'inactive' ? "bg-red-500" : "bg-gray-400"
              )} />
              
              {/* Well Info - Clickable to show details */}
              <button 
                onClick={() => handleWellSelect(well)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="flex items-center gap-1.5">
                  <p className={cn(
                    "font-medium truncate",
                    isDarkMode ? "text-white" : "text-gray-900"
                  )}>
                    {well.name}
                  </p>
                  {/* Feature 1: Show favorite star on list item */}
                  {favoriteWells.has(well.id) && (
                    <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 flex-shrink-0" />
                  )}
                </div>
                <p className={cn(
                  "text-xs truncate",
                  isDarkMode ? "text-slate-400" : "text-gray-500"
                )}>
                  {well.operator} • {well.api}
                </p>
              </button>
              
              {/* Feature 1: Quick Favorite Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFavorite(well);
                }}
                className={cn(
                  "p-2 rounded-lg transition-all active:scale-90",
                  favoriteWells.has(well.id) 
                    ? "text-amber-400" 
                    : isDarkMode ? "text-slate-500 hover:text-amber-400" : "text-gray-400 hover:text-amber-500"
                )}
              >
                <Star className={cn(
                  "h-5 w-5",
                  favoriteWells.has(well.id) && "fill-current"
                )} />
              </button>
              
              {/* NAV Button - Direct navigation */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToWell(well);
                }}
                className={cn(
                  "flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
                  "bg-blue-600 hover:bg-blue-500 text-white shadow-md active:scale-95"
                )}
              >
                <Navigation className="h-3.5 w-3.5" />
                NAV
              </button>
            </div>
          ))}
          </div>
          
          {/* Load More Button */}
          {hasMoreWells && !showRecentWells && (
            <div className="p-4">
              <button
                onClick={() => setDisplayLimit(prev => prev + 100)}
                className={cn(
                  "w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]",
                  isDarkMode 
                    ? "bg-slate-800 hover:bg-slate-700 text-white border border-slate-700" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-200"
                )}
              >
                Load More Wells ({Math.min(100, totalWellsCount - displayLimit)} more)
              </button>
              <p className={cn(
                "text-center text-xs mt-2",
                isDarkMode ? "text-slate-500" : "text-gray-400"
              )}>
                Showing {displayWells.length} of {totalWellsCount.toLocaleString()}
              </p>
            </div>
          )}
          
          {/* Bottom padding for safe area */}
          <div className="h-6" />
        </div>
      </div>
    );
  };
  
  // =========== LOADING / ERROR STATES ===========
  
  if (loadError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900">
        <div className="text-center text-white p-8">
          <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold mb-2">Map Failed to Load</h2>
          <p className="text-slate-400 mb-4">Please check your internet connection</p>
          <Button onClick={onClose}>Go Back</Button>
        </div>
      </div>
    );
  }
  
  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-lg">Loading Industry Map...</p>
        </div>
      </div>
    );
  }
  
  // =========== MAIN RENDER ===========
  
  return (
    <div className="fixed inset-0 z-50">
      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onMapLoad}
        onZoomChanged={handleZoomChanged}
        onIdle={handleMapIdle}
        options={getMapOptions()}
      >
        {/* User Location Marker with Heading Arrow */}
        {userLocation && (
          <>
            {/* Accuracy circle (subtle) */}
            <Marker
              position={{ lat: userLocation.lat, lng: userLocation.lng }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#3B82F6',
                fillOpacity: 0.15,
                strokeColor: '#3B82F6',
                strokeWeight: 1,
                scale: 30,
              }}
              zIndex={998}
            />
            
            {/* Heading arrow (shows which way phone is pointing) */}
            {userHeading !== null && (
              <Marker
                position={{ lat: userLocation.lat, lng: userLocation.lng }}
                icon={{
                  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  fillColor: '#3B82F6',
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                  scale: 6,
                  rotation: userHeading,
                  anchor: new google.maps.Point(0, 2.5),
                }}
                zIndex={1001}
              />
            )}
            
            {/* Center dot */}
            <Marker
              position={{ lat: userLocation.lat, lng: userLocation.lng }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#3B82F6',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 3,
                scale: 10,
              }}
              zIndex={1000}
            />
          </>
        )}
        
        {/* Background Wells (shown before search when zoomed in) - Feature 5 */}
        {!selectedCompany && !nearMeMode && backgroundWells.length > 0 && (
          <MarkerClusterer>
            {(clusterer) => (
              <>
                {backgroundWells.map((well) => (
                  <Marker
                    key={`bg-${well.id}`}
                    position={{ lat: well.lat, lng: well.lng }}
                    onClick={() => handleWellSelect(well)}
                    clusterer={clusterer}
                    icon={createWellMarkerSVG(
                      favoriteWells.has(well.id) ? '#F59E0B' : STATUS_COLORS[well.formattedStatus],
                      selectedWell?.id === well.id
                    )}
                    zIndex={selectedWell?.id === well.id ? 999 : favoriteWells.has(well.id) ? 100 : 1}
                    title={`${well.name} - ${well.operator}`}
                  />
                ))}
              </>
            )}
          </MarkerClusterer>
        )}
        
        {/* Well Markers - Feature 2: With Clustering (from search results) */}
        {(selectedCompany || nearMeMode) && clusteringEnabled && filteredWells.length > 10 ? (
          <MarkerClusterer>
            {(clusterer) => (
              <>
                {filteredWells.map((well) => (
                  <Marker
                    key={well.id}
                    position={{ lat: well.lat, lng: well.lng }}
                    onClick={() => handleWellSelect(well)}
                    clusterer={clusterer}
                    icon={createWellMarkerSVG(
                      favoriteWells.has(well.id) ? '#F59E0B' : STATUS_COLORS[well.formattedStatus],
                      selectedWell?.id === well.id
                    )}
                    zIndex={selectedWell?.id === well.id ? 999 : favoriteWells.has(well.id) ? 100 : 1}
                    title={well.name}
                  />
                ))}
              </>
            )}
          </MarkerClusterer>
        ) : (selectedCompany || nearMeMode) ? (
          // Without clustering (zoomed in) - only show when company selected or near me mode
          filteredWells.map((well) => (
            <Marker
              key={well.id}
              position={{ lat: well.lat, lng: well.lng }}
              onClick={() => handleWellSelect(well)}
              icon={createWellMarkerSVG(
                favoriteWells.has(well.id) ? '#F59E0B' : STATUS_COLORS[well.formattedStatus],
                selectedWell?.id === well.id
              )}
              zIndex={selectedWell?.id === well.id ? 999 : favoriteWells.has(well.id) ? 100 : 1}
              title={well.name}
            />
          ))
        ) : null}
      </GoogleMap>
      
      {/* Search Bar */}
      {renderSearchBar()}
      
      {/* Map Controls */}
      {renderMapControls()}
      
      {/* Wells List (when company selected) */}
      {renderWellsList()}
      
      {/* Well Bottom Sheet (when well selected) */}
      <AnimatePresence>
        {selectedWell && renderWellBottomSheet()}
      </AnimatePresence>
      
      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoadingWells && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          >
            <div className={cn(
              "p-6 rounded-2xl text-center",
              isDarkMode ? "bg-slate-800" : "bg-white"
            )}>
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className={cn("font-medium", isDarkMode ? "text-white" : "text-gray-900")}>
                Loading {selectedCompany?.name} wells...
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
